import { Extension } from '@tiptap/core';
export const DEFAULT_BREAKPOINTS = ['lg', 'md', 'sm'];
export function responsiveVarName(cssProp, breakpoint, prefix = 'vp') {
    // cssProp="font-size" + breakpoint="lg" => "--vp-font-size-lg"
    return `--${prefix}-${cssProp}-${breakpoint}`;
}
export function parseStyleString(style) {
    const out = {};
    if (!style || typeof style !== 'string')
        return out;
    style
        .split(';')
        .map(s => s.trim())
        .filter(Boolean)
        .forEach(chunk => {
        const idx = chunk.indexOf(':');
        if (idx === -1)
            return;
        const key = chunk.slice(0, idx).trim();
        const val = chunk.slice(idx + 1).trim();
        if (!key)
            return;
        out[key] = val;
    });
    return out;
}
export function stringifyStyleObject(obj) {
    const entries = Object.entries(obj)
        .filter(([, v]) => v !== null && v !== undefined && `${v}`.length);
    if (!entries.length)
        return '';
    return `${entries.map(([k, v]) => `${k}: ${v}`).join('; ')};`;
}
export function setStyleKey(styleString, key, value) {
    const styleObj = parseStyleString(styleString);
    styleObj[key] = value;
    return stringifyStyleObject(styleObj);
}
export function unsetStyleKey(styleString, key) {
    const styleObj = parseStyleString(styleString);
    if (!(key in styleObj))
        return styleString || '';
    delete styleObj[key];
    return stringifyStyleObject(styleObj);
}
export function getStyleKey(styleString, key) {
    var _a;
    const styleObj = parseStyleString(styleString);
    return (_a = styleObj[key]) !== null && _a !== void 0 ? _a : null;
}
export function hasAnyResponsiveVars(styleString, prefix = 'vp') {
    const s = styleString || '';
    return s.includes(`--${prefix}-`);
}
export const ResponsiveEngine = Extension.create({
    name: 'responsiveEngine',
    addOptions() {
        return {
            prefix: 'vp',
            breakpoints: DEFAULT_BREAKPOINTS,
            defaultBreakpoint: 'lg',
            defaultMarkType: 'textStyle',
        };
    },
    addStorage() {
        return {
            breakpoint: this.options.defaultBreakpoint,
        };
    },
    addCommands() {
        const ensureBp = (bp) => {
            var _a, _b;
            const resolved = bp || ((_b = (_a = this.editor.storage) === null || _a === void 0 ? void 0 : _a.responsiveEngine) === null || _b === void 0 ? void 0 : _b.breakpoint) || this.options.defaultBreakpoint;
            // If user passed an unknown breakpoint, don’t mutate.
            if (!this.options.breakpoints.includes(resolved))
                return null;
            return resolved;
        };
        return {
            setResponsiveBreakpoint: (breakpoint) => () => {
                if (!this.options.breakpoints.includes(breakpoint))
                    return false;
                this.storage.breakpoint = breakpoint;
                return true;
            },
            setResponsiveMarkStyle: (cssProp, value, opts) => ({ editor, chain }) => {
                const bp = ensureBp(opts === null || opts === void 0 ? void 0 : opts.breakpoint);
                if (!bp)
                    return false;
                const markType = (opts === null || opts === void 0 ? void 0 : opts.markType) || this.options.defaultMarkType;
                const varName = responsiveVarName(cssProp, bp, this.options.prefix);
                const attrs = editor.getAttributes(markType) || {};
                const nextStyle = setStyleKey(attrs.style || '', varName, value);
                return chain()
                    .setMark(markType, { ...attrs, style: nextStyle })
                    .run();
            },
            unsetResponsiveMarkStyle: (cssProp, opts) => ({ editor, chain }) => {
                const bp = ensureBp(opts === null || opts === void 0 ? void 0 : opts.breakpoint);
                if (!bp)
                    return false;
                const markType = (opts === null || opts === void 0 ? void 0 : opts.markType) || this.options.defaultMarkType;
                const varName = responsiveVarName(cssProp, bp, this.options.prefix);
                const attrs = editor.getAttributes(markType) || {};
                const nextStyle = unsetStyleKey(attrs.style || '', varName);
                // IMPORTANT: Do NOT call removeEmptyTextStyle automatically.
                // There are known issues where it can remove other styles in multi-span selections.
                // If you want cleanup, do it in your UI intentionally (e.g. on collapsed selection).
                return chain()
                    .setMark(markType, { ...attrs, style: nextStyle })
                    .run();
            },
            getResponsiveMarkStyle: (cssProp, opts) => ({ editor }) => {
                const bp = ensureBp(opts === null || opts === void 0 ? void 0 : opts.breakpoint);
                if (!bp)
                    return null;
                const markType = (opts === null || opts === void 0 ? void 0 : opts.markType) || this.options.defaultMarkType;
                const varName = responsiveVarName(cssProp, bp, this.options.prefix);
                const attrs = editor.getAttributes(markType) || {};
                return getStyleKey(attrs.style || '', varName);
            },
            setResponsiveNodeStyle: (nodeType, cssProp, value, opts) => ({ editor, chain }) => {
                const bp = ensureBp(opts === null || opts === void 0 ? void 0 : opts.breakpoint);
                if (!bp)
                    return false;
                const varName = responsiveVarName(cssProp, bp, this.options.prefix);
                const attrs = editor.getAttributes(nodeType) || {};
                const nextStyle = setStyleKey(attrs.style || '', varName, value);
                return chain()
                    .updateAttributes(nodeType, { ...attrs, style: nextStyle })
                    .run();
            },
            unsetResponsiveNodeStyle: (nodeType, cssProp, opts) => ({ editor, chain }) => {
                const bp = ensureBp(opts === null || opts === void 0 ? void 0 : opts.breakpoint);
                if (!bp)
                    return false;
                const varName = responsiveVarName(cssProp, bp, this.options.prefix);
                const attrs = editor.getAttributes(nodeType) || {};
                const nextStyle = unsetStyleKey(attrs.style || '', varName);
                return chain()
                    .updateAttributes(nodeType, { ...attrs, style: nextStyle })
                    .run();
            },
            getResponsiveNodeStyle: (nodeType, cssProp, opts) => ({ editor }) => {
                const bp = ensureBp(opts === null || opts === void 0 ? void 0 : opts.breakpoint);
                if (!bp)
                    return null;
                const varName = responsiveVarName(cssProp, bp, this.options.prefix);
                const attrs = editor.getAttributes(nodeType) || {};
                return getStyleKey(attrs.style || '', varName);
            },
        };
    },
});
// ------------------------------------------------------------
// Production tips (keep near the engine for future maintainers)
// ------------------------------------------------------------
/**
 * CSS pattern (example) you should generate once in your app stylesheet:
 *
 * .editor.vp-lg [style*="--vp-font-size-lg"] { font-size: var(--vp-font-size-lg) }
 * .editor.vp-md [style*="--vp-font-size-md"] { font-size: var(--vp-font-size-md) }
 * .editor.vp-sm [style*="--vp-font-size-sm"] { font-size: var(--vp-font-size-sm) }
 *
 * Keep it explicit per property for best perf (no heavy :has() / attribute scanning).
 */ 
